import { useCallback, useEffect, useState } from "react";
import { useCopyProjectAsyncMutation } from "../../../app/features/project/projectApiSlice";
import { useLazyGetFastActivitiesByProjectQuery } from "../../../app/features/activities/activityApiSlice";
import { Tool } from "../../../app/features/auth/authTypes";
import { FastProject } from "../../../types/interfaces";
import { EventTypes, useFirebaseHook } from "../../../utils/firebaseUtils";
import useErrorMessage from "../../../utils/useErrorMessage";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setAsyncJob, selectActiveCopyJobForNewProject, removeAsyncJob } from "../../../app/features/asyncJobs/asyncJobsSlice";
import type { ErrorType } from "../../../types/interfaces";
import type { AsyncJob, CopyAsyncResponse } from "../../../app/features/asyncJobs/asyncJobsTypes";
import type { FastActivityResponse } from "../../../app/features/activities/activityTypes";

interface UseCopyProjectReturn {
    handleDuplicateProject: (project: FastProject) => Promise<void>;
    isLoadingCopy: boolean;
    copyProjectErrorMsg: string;
    isCopyProjectErrorMessageOpen: boolean;
    closeCopyProjectErrorMessage: () => void;
    isDuplicateWarningOpen: boolean;
    handleConfirmDuplicate: () => Promise<void>;
    handleCancelDuplicate: () => void;
}

const useCopyProject = (refetch: () => void): UseCopyProjectReturn => {
    const [copyProjectAsync, { isLoading: isMutationLoading, isError, error }] = useCopyProjectAsyncMutation();
    const [getFastActivities] = useLazyGetFastActivitiesByProjectQuery();
    const [isCopyProjectErrorMessageOpen, setIsCopyProjectErrorMessageOpen] = useState(false);
    const [isDuplicateWarningOpen, setIsDuplicateWarningOpen] = useState(false);
    const [pendingProject, setPendingProject] = useState<FastProject | null>(null);
    const [activeProjectId, setActiveProjectId] = useState<number | null>(null);
    const { fireEvent } = useFirebaseHook();
    const { errorMsg } = useErrorMessage({ isError, error: error as ErrorType | null });
    const dispatch = useAppDispatch();
    
    const activeJob = useAppSelector(state => 
        activeProjectId ? selectActiveCopyJobForNewProject(state, activeProjectId) : null
    );

    const isLoadingCopy = isMutationLoading || !!activeJob;

    const closeCopyProjectErrorMessage = useCallback(() => {
        setIsCopyProjectErrorMessageOpen(false);
    }, []);

    useEffect(() => {
        if (errorMsg) {
            setIsCopyProjectErrorMessageOpen(true);
        }
    }, [errorMsg]);

    useEffect(() => {
        if (!activeJob) return;
        if (activeJob.status === 'failed') {
            refetch();
            setActiveProjectId(null);
            dispatch(removeAsyncJob(activeJob.id));
        }
    }, [activeJob, refetch, dispatch]);

    const executeCopy = useCallback(async (project: FastProject) => {
        try {
            if (!project?.id) return;
            
            setActiveProjectId(project.id);
            fireEvent(EventTypes.duplicate_project);
            
            const response = await copyProjectAsync(project.id).unwrap() as CopyAsyncResponse;
            
            if (response.status === 'completed' && response.project) {
                await refetch();
                setActiveProjectId(null);
            } else if (response.status === 'pending' && response.job_id) {
                const initialJob: AsyncJob = {
                    id: response.job_id,
                    kind: 'project_copy',
                    status: 'pending',
                    progress: 0,
                    result: response.new_project_id ? { new_project_id: response.new_project_id } : null,
                    error_message: null,
                    project: response.new_project_id ?? null,
                    created_at: new Date().toISOString(),
                    started_at: null,
                    completed_at: null,
                };
                dispatch(setAsyncJob(initialJob));
            }
        } catch (error) {
            console.error('Failed to duplicate project:', error);
            setActiveProjectId(null);
        }
    }, [copyProjectAsync, refetch, fireEvent, dispatch]);

    const handleDuplicateProject = useCallback(async (project: FastProject) => {
        if (!project?.id || isLoadingCopy) return;

        try {
            const result = await getFastActivities({ projectId: project.id, pageSize: 1, tool: Tool.B_INTACT }).unwrap() as FastActivityResponse;
            if (result.count > 0) {
                setPendingProject(project);
                setIsDuplicateWarningOpen(true);
            } else {
                await executeCopy(project);
            }
        } catch {
            await executeCopy(project);
        }
    }, [getFastActivities, executeCopy, isLoadingCopy]);

    const handleConfirmDuplicate = useCallback(async () => {
        setIsDuplicateWarningOpen(false);
        if (pendingProject) {
            await executeCopy(pendingProject);
            setPendingProject(null);
        }
    }, [pendingProject, executeCopy]);

    const handleCancelDuplicate = useCallback(() => {
        setIsDuplicateWarningOpen(false);
        setPendingProject(null);
    }, []);

    return {
        handleDuplicateProject,
        isLoadingCopy,
        copyProjectErrorMsg: errorMsg,
        isCopyProjectErrorMessageOpen,
        closeCopyProjectErrorMessage,
        isDuplicateWarningOpen,
        handleConfirmDuplicate,
        handleCancelDuplicate,
    };
};

export default useCopyProject; 